/**
 * "Build for devices" of a mobile service (Expo / React Native / Flutter): Android or iOS, Debug (internal testing) or
 * Release (the stores), and who builds it where there is a choice (Android: Flow in your cluster, Expo EAS, Codemagic;
 * iOS: Expo EAS for Expo / React Native, Codemagic; and "My Mac", one of the person's Macs running `flow builder
 * start`, listed with online / offline, only when they have one). POST /device-builds.
 * - Codemagic: the app and workflow can be picked; without a pick the server takes the codemagic.yaml workflow for the
 *   platform and kind (shown) and the app named like the service. No codemagic.yaml: "Add codemagic.yaml" writes the
 *   one Flow makes (POST /device-builds/codemagic-yaml), to push to the app's repository.
 * - Builds off here (`available.enabled` false): its reason, with Change plan when the plan does not include them.
 * - A provider whose token the person has not connected (GET /me/device-builds/connections, or a 409 `connect_expo` /
 *   `connect_codemagic`): ConnectProviderPanel inline, then the build can start.
 * - A release Android build by Flow without a keystore (GET device-signing not configured, or a 409 `no_signing`):
 *   SigningForm inline.
 * `app` is the service's entry in GET /device-builds › apps, `available` that answer's availability.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Input, Modal, Radio, Select, Switch } from 'antd';
import {
  addCodemagicYaml, getCodemagicApps, getConnections, getDeviceSigning, getRunners, notifyDeviceBuildsChanged, startDeviceBuild,
} from '../../services/DeviceBuildsService';
import { PlanChangeButton } from '../sandbox/PlanRequest';
import ConnectProviderPanel from './ConnectProviderPanel';
import SigningForm from './SigningForm';
import {
  PLATFORM_LABEL, PROVIDER_LABEL, VARIANT_HINT, VARIANT_LABEL, appKindOf, buildOutcome, connectionOf, pickWorkflow,
} from './deviceBuildsText';
import './DeviceBuilds.css';

const PLATFORMS = ['android', 'ios'];
const providersOf = (app, platform) => app?.platforms?.[platform]?.providers || [];
/** Flow builds Android itself where its cluster can; else the other provider. */
export function defaultProvider(app, platform, available) {
  const providers = providersOf(app, platform);
  if (providers.includes('flow') && available?.androidInCluster !== false) { return 'flow'; }
  return providers.find((p) => p !== 'flow' && p !== 'mac') || providers[0] || null;
}

const CONNECT_CODES = { connect_expo: 'expo', connect_codemagic: 'codemagic' };

export default function DeviceBuildDialog({ open, onClose, projectId, app, available = null, onStarted = null }) {
  const platforms = PLATFORMS.filter((p) => providersOf(app, p).length > 0);
  const [platform, setPlatform] = useState(platforms[0] || 'android');
  const [variant, setVariant] = useState('debug');
  const [provider, setProvider] = useState(() => defaultProvider(app, platforms[0] || 'android', available));
  const [submit, setSubmit] = useState(false);
  const [codemagic, setCodemagic] = useState({ appId: null, workflowId: null, branch: '' });
  const [connections, setConnections] = useState(null);
  const [forcedConnect, setForcedConnect] = useState(null);
  const [signing, setSigning] = useState({ loading: false, value: null });
  const [forcedSigning, setForcedSigning] = useState(false);
  const [replaceSigning, setReplaceSigning] = useState(false);
  const [cmApps, setCmApps] = useState({ loading: false, value: null, error: null });
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [planBlocked, setPlanBlocked] = useState(null);
  const [runners, setRunners] = useState([]);
  const [runnerId, setRunnerId] = useState(null);
  const [cmYaml, setCmYaml] = useState({ saving: false, value: null, error: null });

  const enabled = available?.enabled !== false;
  const connection = connectionOf(provider);
  const flowRelease = platform === 'android' && provider === 'flow' && variant === 'release';

  // Every opening starts from the defaults; the person's connections are read once per opening.
  useEffect(() => {
    if (!open) { return undefined; }
    const first = platforms[0] || 'android';
    setPlatform(first);
    setVariant('debug');
    setProvider(defaultProvider(app, first, available));
    setSubmit(false);
    setCodemagic({ appId: null, workflowId: null, branch: '' });
    setForcedConnect(null);
    setForcedSigning(false);
    setReplaceSigning(false);
    setError(null);
    setPlanBlocked(null);
    setRunnerId(null);
    setCmYaml({ saving: false, value: null, error: null });
    let live = true;
    setConnections(null);
    getConnections().then((answer) => { if (live) { setConnections(answer); } }).catch(() => { if (live) { setConnections(null); } });
    setRunners([]);
    getRunners().then((answer) => { if (live) { setRunners(answer?.runners || []); } }).catch(() => { if (live) { setRunners([]); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, app?.service]);

  // A release Android build by Flow needs the service's keystore: is there one?
  useEffect(() => {
    const name = app?.service;
    if (!open || !flowRelease || !name) { return undefined; }
    let live = true;
    setSigning({ loading: true, value: null });
    getDeviceSigning(projectId, name)
      .then((value) => { if (live) { setSigning({ loading: false, value }); } })
      .catch(() => { if (live) { setSigning({ loading: false, value: null }); } });
    return () => { live = false; };
  }, [open, flowRelease, projectId, app?.service]);

  const connectInfo = connection ? connections?.providers?.[connection] || null : null;
  const connected = connection ? Boolean(connectInfo?.connected) : true;
  const needsConnect = Boolean(connection) && (forcedConnect === connection || (connections !== null && !connected));

  // Codemagic's apps and workflows, once the person's Codemagic is connected.
  useEffect(() => {
    if (!open || provider !== 'codemagic' || needsConnect) { return undefined; }
    let live = true;
    setCmApps({ loading: true, value: null, error: null });
    getCodemagicApps()
      .then((answer) => { if (live) { setCmApps({ loading: false, value: answer?.apps || [], error: null }); } })
      .catch((err) => { if (live) { setCmApps({ loading: false, value: null, error: err }); } });
    return () => { live = false; };
  }, [open, provider, needsConnect]);

  // The person's Macs for this platform; "My Mac" is offered only when there is one (online or not).
  const platformRunners = runners.filter((r) => !r.platforms || r.platforms.includes(platform));
  const onlineRunners = platformRunners.filter((r) => r.online);
  const chosenRunner = provider === 'mac' ? (onlineRunners.find((r) => r.id === runnerId) || onlineRunners[0] || null) : null;

  // Codemagic's codemagic.yaml (GET /device-builds › apps[].codemagic, or the one just added here).
  const cmInfo = cmYaml.value || app?.codemagic || null;
  const autoWorkflow = cmInfo?.yaml ? pickWorkflow(cmInfo.workflows, platform, variant) : null;
  const yamlWorkflows = cmInfo?.workflows || [];

  const signingInfo = signing.value;
  const needsSigning = flowRelease && (forcedSigning || replaceSigning || (signingInfo !== null && !signingInfo.configured));
  const cmApp = (cmApps.value || []).find((item) => item.id === codemagic.appId) || null;
  const cmAppCount = (cmApps.value || []).length;
  // With what the server knows of codemagic.yaml, the app and workflow may be left to it; without, both are picked.
  const codemagicReady = provider !== 'codemagic' || (cmInfo
    ? Boolean((codemagic.appId || cmAppCount > 0) && (codemagic.workflowId || autoWorkflow))
    : Boolean(codemagic.appId && codemagic.workflowId));
  const macReady = provider !== 'mac' || Boolean(chosenRunner);
  const canStart = enabled && !planBlocked && Boolean(provider) && !needsConnect && !needsSigning && codemagicReady && macReady && !(flowRelease && signing.loading);

  const pickPlatform = (next) => {
    setPlatform(next);
    setProvider(defaultProvider(app, next, available));
    setSubmit(false);
    setError(null);
  };
  const pickProvider = (next) => { setProvider(next); setError(null); };

  const start = async() => {
    setStarting(true);
    setError(null);
    try {
      const answer = await startDeviceBuild(projectId, {
        service: app.service,
        platform,
        variant,
        provider,
        submit,
        codemagic: provider === 'codemagic'
          ? {
            ...(codemagic.appId ? { appId: codemagic.appId } : {}),
            ...(codemagic.workflowId || autoWorkflow ? { workflowId: codemagic.workflowId || autoWorkflow } : {}),
            ...(codemagic.branch.trim() ? { branch: codemagic.branch.trim() } : {}),
          }
          : null,
        ...(provider === 'mac' && chosenRunner ? { runnerId: chosenRunner.id } : {}),
      });
      notifyDeviceBuildsChanged(projectId);
      onStarted?.(answer?.build || null);
      onClose();
    } catch (err) {
      if (CONNECT_CODES[err?.code]) { setForcedConnect(CONNECT_CODES[err.code]); } else if (err?.code === 'no_signing') { setForcedSigning(true); } else if (err?.code === 'not_in_plan') { setPlanBlocked(err.message); } else { setError(err?.message || 'The build could not start.'); }
    } finally {
      setStarting(false);
    }
  };

  const addYaml = async() => {
    setCmYaml({ saving: true, value: null, error: null });
    try {
      const answer = await addCodemagicYaml(projectId, app.service);
      setCmYaml({ saving: false, value: { yaml: true, error: null, workflows: answer?.workflows || [], path: answer?.path || null, created: Boolean(answer?.created) }, error: null });
    } catch (err) {
      setCmYaml({ saving: false, value: null, error: err?.message || 'codemagic.yaml could not be added.' });
    }
  };

  const providers = providersOf(app, platform).filter((p) => p !== 'mac' || platformRunners.length > 0);
  const footer = enabled ? [
    <Button key="cancel" onClick={onClose}>Cancel</Button>,
    <Button data-testid="devb-start" disabled={!canStart} key="start" loading={starting} onClick={start} type="primary">Start build</Button>,
  ] : [<Button key="close" onClick={onClose}>Close</Button>];

  return (
    <Modal destroyOnHidden footer={footer} onCancel={onClose} open={open} title={`Build ${app?.service || ''} for devices`} width={600} zIndex={1200}>
      <div className="devb-dialog" data-testid="devb-dialog">
        {app?.label && <p className="devb-muted" data-testid="devb-app-kind">{appKindOf(app.label)}.</p>}
        {!enabled ? (
          <Alert
            data-testid="devb-unavailable"
            description={available?.code === 'not_in_plan' ? <PlanChangeButton /> : null}
            showIcon
            title={available?.reason || 'Device builds are not available here.'}
            type="info"
          />
        ) : (
          <>
            <div className="devb-field">
              <span>Platform</span>
              <Radio.Group buttonStyle="solid" data-testid="devb-platform" onChange={(e) => pickPlatform(e.target.value)} optionType="button" value={platform}>
                {platforms.map((p) => <Radio key={p} value={p}>{PLATFORM_LABEL[p]}</Radio>)}
              </Radio.Group>
            </div>
            <div className="devb-field">
              <span>Kind of build</span>
              <Radio.Group data-testid="devb-variant" onChange={(e) => { setVariant(e.target.value); setError(null); }} value={variant}>
                {['debug', 'release'].map((v) => <Radio key={v} value={v}>{VARIANT_LABEL[v]} <span className="devb-muted">({VARIANT_HINT[v]})</span></Radio>)}
              </Radio.Group>
            </div>
            {providers.length > 1 ? (
              <div className="devb-field">
                <span>Built by</span>
                <Radio.Group data-testid="devb-provider" onChange={(e) => pickProvider(e.target.value)} value={provider}>
                  {providers.map((p) => {
                    const noCluster = p === 'flow' && available?.androidInCluster === false;
                    const macOffline = p === 'mac' && onlineRunners.length === 0;
                    const off = noCluster || macOffline;
                    const why = noCluster ? 'This Flow cannot run build jobs in its cluster.' : macOffline ? 'Your Mac is offline: run "flow builder start" on it.' : undefined;
                    return (
                      <Radio disabled={off} key={p} title={why} value={p}>
                        {PROVIDER_LABEL[p]}
                      </Radio>
                    );
                  })}
                </Radio.Group>
              </div>
            ) : provider && <p className="devb-muted" data-testid="devb-provider-one">Built by {PROVIDER_LABEL[provider]}.</p>}
            {provider === 'mac' && (
              <div className="devb-panel" data-testid="devb-mac">
                <label className="devb-field">
                  <span>Mac</span>
                  <Select
                    aria-label="Mac"
                    data-testid="devb-mac-runner"
                    onChange={setRunnerId}
                    options={platformRunners.map((r) => ({ value: r.id, label: `${r.name} · ${r.online ? (r.busy ? 'online, building' : 'online') : 'offline'}`, disabled: !r.online }))}
                    value={chosenRunner?.id || null}
                  />
                </label>
                <p className="devb-muted">It runs on the Mac with its own Xcode, Android SDK and signing identity; Flow sends it the source and gets back the app and the log.</p>
              </div>
            )}
            {!providers.includes('mac') && runners.length === 0 && (
              <p className="devb-muted" data-testid="devb-mac-hint">Build on your own Mac too: run <code>flow build {app?.service} --platform {platform}</code>, or <code>flow builder start</code> to pick it here (Flow CLI).</p>
            )}
            <p className="devb-outcome" data-testid="devb-outcome">{buildOutcome({ platform, variant, provider })}</p>
            {provider === 'eas' && platform === 'ios' && variant === 'release' && (
              <label className="devb-switch">
                <Switch checked={submit} data-testid="devb-submit" onChange={setSubmit} size="small" />
                <span>Also submit to TestFlight (Apple&apos;s beta testing)</span>
              </label>
            )}
            {app?.notes?.length > 0 && (
              <ul className="devb-notes" data-testid="devb-notes">{app.notes.map((note) => <li key={note}>{note}</li>)}</ul>
            )}

            {connection && connected && connectInfo?.account && !needsConnect && (
              <p className="devb-muted" data-testid="devb-connected">Your {connection === 'expo' ? 'Expo' : 'Codemagic'} account: {connectInfo.account}.</p>
            )}
            {needsConnect && (
              <ConnectProviderPanel
                available={connections?.available !== false}
                info={connectInfo}
                onConnected={(next) => {
                  setConnections((prev) => ({
                    ...(prev || {}),
                    available: true,
                    providers: { ...(prev?.providers || {}), [connection]: { ...(prev?.providers?.[connection] || {}), ...next, connected: true } },
                  }));
                  setForcedConnect(null);
                }}
                provider={connection}
                reason={connections?.reason || null}
              />
            )}

            {provider === 'codemagic' && !needsConnect && cmInfo && !cmInfo.yaml && (
              <Alert
                data-testid="devb-codemagic-noyaml"
                description={(
                  <>
                    <p>Codemagic builds with the codemagic.yaml of the app&apos;s Git repository, and this service has none. Flow can add one with a workflow for each platform and kind; then push it (Publish › Git) and add the app in Codemagic.</p>
                    {cmYaml.error && <p className="devb-bad">{cmYaml.error}</p>}
                    <Button data-testid="devb-codemagic-addyaml" loading={cmYaml.saving} onClick={addYaml} size="small">Add codemagic.yaml</Button>
                  </>
                )}
                showIcon
                title="No codemagic.yaml"
                type="warning"
              />
            )}
            {provider === 'codemagic' && cmYaml.value?.created && (
              <p className="devb-muted" data-testid="devb-codemagic-added">Added {cmYaml.value.path}. Push it to the app&apos;s Git repository (Publish › Git) before you build.</p>
            )}
            {provider === 'codemagic' && cmInfo?.error && <p className="devb-bad" data-testid="devb-codemagic-yaml-error">{cmInfo.error}</p>}
            {provider === 'codemagic' && !needsConnect && (!cmInfo || cmInfo.yaml) && (
              <div className="devb-panel" data-testid="devb-codemagic">
                {cmApps.loading ? <p className="devb-muted">Reading your Codemagic apps…</p>
                  : cmApps.error ? <p className="devb-bad">{cmApps.error.message || 'Your Codemagic apps could not be read.'}</p>
                    : (cmApps.value || []).length === 0 ? <p className="devb-muted">No apps in your Codemagic account yet: add this repository in Codemagic first.</p>
                      : (
                        <>
                          <label className="devb-field">
                            <span>Codemagic app</span>
                            <Select
                              aria-label="Codemagic app"
                              data-testid="devb-codemagic-app"
                              onChange={(appId) => setCodemagic((prev) => ({ ...prev, appId, workflowId: null }))}
                              options={cmApps.value.map((item) => ({ value: item.id, label: item.repository ? `${item.name} · ${item.repository}` : item.name }))}
                              placeholder={cmInfo && cmAppCount === 1 ? `${cmApps.value[0].name} (your only app)` : cmInfo ? 'The app named like the service, or pick one' : 'Pick the app'}
                              value={codemagic.appId}
                            />
                          </label>
                          <label className="devb-field">
                            <span>Workflow</span>
                            <Select
                              aria-label="Codemagic workflow"
                              data-testid="devb-codemagic-workflow"
                              disabled={!cmApp && yamlWorkflows.length === 0}
                              onChange={(workflowId) => setCodemagic((prev) => ({ ...prev, workflowId }))}
                              options={[...yamlWorkflows, ...(cmApp?.workflows || []).filter((w) => !yamlWorkflows.some((y) => y.id === w.id))].map((w) => ({ value: w.id, label: w.name }))}
                              placeholder={autoWorkflow ? `From codemagic.yaml: ${autoWorkflow}` : 'Pick the workflow'}
                              value={codemagic.workflowId}
                            />
                          </label>
                          <label className="devb-field">
                            <span>Branch (optional)</span>
                            <Input onChange={(e) => setCodemagic((prev) => ({ ...prev, branch: e.target.value }))} placeholder="main" value={codemagic.branch} />
                          </label>
                        </>
                      )}
              </div>
            )}

            {flowRelease && signingInfo?.available === false && (
              <p className="devb-bad" data-testid="devb-signing-unavailable">{signingInfo.reason || 'This Flow cannot keep a keystore (it has no secret store).'}</p>
            )}
            {flowRelease && signingInfo?.configured && !needsSigning && (
              <p className="devb-muted" data-testid="devb-signing-configured">
                Signed with the key “{signingInfo.keyAlias}”.{' '}
                <button className="devb-link" onClick={() => setReplaceSigning(true)} type="button">Replace the keystore</button>
              </p>
            )}
            {needsSigning && signingInfo?.available !== false && (
              <SigningForm
                onCancel={replaceSigning ? () => setReplaceSigning(false) : null}
                onSaved={(value) => { setSigning({ loading: false, value: { ...(value || {}), configured: true } }); setForcedSigning(false); setReplaceSigning(false); }}
                projectId={projectId}
                service={app.service}
              />
            )}

            {planBlocked && (
              <Alert data-testid="devb-plan" description={<PlanChangeButton />} showIcon title={planBlocked} type="info" />
            )}
            {error && <Alert className="devb-alert" data-testid="devb-error" showIcon title={error} type="error" />}
          </>
        )}
      </div>
    </Modal>
  );
}

DeviceBuildDialog.propTypes = {
  // `{ service, framework, label, platforms: { android: { providers }, ios: { providers } }, notes }`.
  app: PropTypes.object,
  // GET /device-builds › available.
  available: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  // (build) => after the build started.
  onStarted: PropTypes.func,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.string.isRequired,
};
